import { useEffect, useMemo, useState } from "react";
import { loadCore, type NameKind, type NamePlaceholder } from "../lib/wasm";

// The name players will read on this build. Three ways to get one, because
// they are genuinely different jobs:
//
//   none      -- the build stays nameless.
//   fixed     -- one name, typed. "Peters Hütte", every time.
//   generated -- drawn from a vocabulary, so a hundred huts do not share a
//                name; the patterns can be edited.
//
// A fixed name is stored as a generator pattern with no placeholders in it --
// the generator then returns exactly that text. Same field, same export path,
// no second mechanism to keep in step.

function parseSpec(spec: string): { kind: string; patterns: string[] } {
  if (!spec) return { kind: "", patterns: [] };
  const parts = spec.split("|");
  // Legacy "de:home" from the first version: the language prefix is gone.
  const kind = parts[0].includes(":") ? parts[0].split(":")[1] : parts[0];
  return { kind, patterns: parts.slice(1).filter(Boolean) };
}

type Mode = "none" | "fixed" | "generated";

export function NamePicker(props: {
  value: string;
  seed: number;
  onChange: (spec: string) => void;
}) {
  const [core, setCore] = useState<Awaited<ReturnType<typeof loadCore>> | null>(null);
  const [kinds, setKinds] = useState<NameKind[]>([]);
  const [holders, setHolders] = useState<NamePlaceholder[]>([]);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    loadCore()
      .then((c) => {
        if (!alive) return;
        setCore(c);
        setKinds(c.nameKinds());
        setHolders(c.namePlaceholders());
      })
      .catch(() => alive && setErr("Renderer-Kern nicht gebaut — keine Vorschau"));
    return () => {
      alive = false;
    };
  }, []);

  const { kind, patterns } = useMemo(() => parseSpec(props.value), [props.value]);
  const current = kinds.find((k) => k.id === kind);

  // A single pattern with no placeholders in it IS a fixed name.
  const fixed =
    patterns.length === 1 && !patterns[0].includes("{") ? patterns[0] : "";
  const mode: Mode = !kind ? "none" : fixed ? "fixed" : "generated";
  const [draft, setDraft] = useState(fixed);
  useEffect(() => setDraft(fixed), [fixed, props.seed]);

  const setSpec = (k: string, pats: string[]) =>
    props.onChange(k ? [k, ...pats.filter((p) => p.trim())].join("|") : "");

  // Any vocabulary works for a fixed name -- nothing is drawn from it.
  const anyKind = kind || kinds[0]?.id || "wohnhaus";
  const setMode = (m: Mode) => {
    if (m === "none") props.onChange("");
    else if (m === "fixed") setSpec(anyKind, [draft.trim() || "Namenlos"]);
    else setSpec(anyKind, []);
  };

  // Live examples and per-line validation, both from the core.
  const [examples, problems] = useMemo(() => {
    if (!core || !kind || mode === "fixed") return [[], []] as [string[], string[]];
    const probs = patterns.map((p) => {
      const r = core.validateNamePattern(p);
      return r.ok ? "" : r.error;
    });
    const ex = probs.some(Boolean)
      ? []
      : core.nameExamples(props.value, 6, props.seed);
    return [ex, probs];
  }, [core, kind, mode, patterns, props.value, props.seed]);

  return (
    <label className="names">
      <select value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
        <option value="none">kein Name</option>
        <option value="fixed">fester Name</option>
        <option value="generated">gewürfelt</option>
      </select>

      {mode === "fixed" && (
        <input
          value={draft}
          placeholder="z. B. „Peters Hütte“"
          onChange={(e) => setDraft(e.target.value)}
          // Stored as a literal pattern: a bar would split the spec, braces
          // would be read as placeholders.
          onBlur={() => {
            const clean = draft.replace(/[|{}]/g, "").trim();
            setDraft(clean);
            if (clean && clean !== fixed) setSpec(anyKind, [clean]);
          }}
        />
      )}

      {mode === "generated" && (
        <>
          <select
            value={kind}
            onChange={(e) => setSpec(e.target.value, [])}
            disabled={!core}
            title="Der Wortschatz, aus dem die Namen gebaut werden."
          >
            {kinds.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>

          <button type="button" onClick={() => setOpen((o) => !o)}>
            {patterns.length
              ? `${patterns.length} eigene Vorlage${patterns.length === 1 ? "" : "n"}`
              : "Standard-Vorlagen"}{" "}
            {open ? "▲" : "▼"}
          </button>

          {open && current && (
            <div className="patterns">
              {(patterns.length ? patterns : current.defaults).map((p, i) => (
                <div key={i} className="row">
                  <input
                    value={p}
                    onChange={(e) => {
                      const next = [...(patterns.length ? patterns : current.defaults)];
                      next[i] = e.target.value;
                      setSpec(kind, next);
                    }}
                  />
                  <button
                    type="button"
                    title="entfernen"
                    onClick={() => {
                      const next = [...(patterns.length ? patterns : current.defaults)];
                      next.splice(i, 1);
                      setSpec(kind, next);
                    }}
                  >
                    −
                  </button>
                </div>
              ))}
              {problems.map((p, i) =>
                p ? (
                  <em key={`e${i}`} className="bad">
                    Zeile {i + 1}: {p}
                  </em>
                ) : null,
              )}
              <div className="row">
                <button
                  type="button"
                  onClick={() =>
                    setSpec(kind, [
                      ...(patterns.length ? patterns : current.defaults),
                      "{art} {adj} {bau}",
                    ])
                  }
                >
                  + Vorlage
                </button>
                <button type="button" onClick={() => setSpec(kind, [])}>
                  Standard
                </button>
              </div>
              <div className="tokens">
                {holders.map((h) => (
                  <span key={h.token} title={`${h.description} — ${h.example}`}>
                    {h.token}
                  </span>
                ))}
              </div>
            </div>
          )}

          <em className="examples">
            {err
              ? err
              : examples.length
                ? examples.join(" · ")
                : problems.some(Boolean)
                  ? "Vorlage fehlerhaft"
                  : "…"}
          </em>
        </>
      )}
    </label>
  );
}
